import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { settingsApi } from '@/lib/api/endpoints';

/**
 * Referência do provedor para o campo "Chamadas simultâneas". Acima do limite normal o
 * Retell não recusa: entra em burst com sobretaxa de US$ 0,10/min na chamada inteira — e a
 * única pista disso é a fatura. Por isso o aviso aqui, no momento de digitar.
 */
export function ConcurrencyHint({ value }: { value: number | undefined }) {
  const q = useQuery({
    queryKey: ['settings', 'concurrency'],
    queryFn: settingsApi.concurrency,
    refetchInterval: 30_000,
  });
  const data = q.data;
  if (!data)
    return <p className="text-xs text-muted-foreground">Consultando o limite do provedor…</p>;
  if (data.provider === 'mock') {
    return (
      <p className="text-xs text-muted-foreground">Simulador: sem limite de simultaneidade.</p>
    );
  }
  const c = data.concurrency;
  if (!c) {
    return (
      <p className="text-xs text-muted-foreground">
        Limite do Retell indisponível{data.error ? `: ${data.error}` : ''}.
      </p>
    );
  }
  const over = value != null && value > c.limit;
  const overBurst = over && c.burstEnabled && c.burstLimit != null && value > c.burstLimit;
  return (
    <>
      <p className="text-xs text-muted-foreground">
        Retell: limite <strong>{c.limit}</strong> ({c.base} base
        {c.purchased ? ` + ${c.purchased} comprados` : ''}) · em uso agora{' '}
        <strong>{c.current}</strong> · burst{' '}
        {c.burstEnabled ? `até ${c.burstLimit ?? '?'}, +US$ 0,10/min` : 'desligado'}
      </p>
      {over && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            {overBurst
              ? `Acima até do burst (${c.burstLimit}): chamadas além disso são recusadas e rediscadas em 5 min.`
              : c.burstEnabled
                ? `Acima do limite normal (${c.limit}): as chamadas de ${c.limit + 1} a ${c.burstLimit} entram em burst, com sobretaxa de US$ 0,10 por minuto na chamada inteira. Compre slots em Billing → Limits ou reduza aqui.`
                : `Acima do limite (${c.limit}): as chamadas além disso serão recusadas pelo Retell e rediscadas em 5 min.`}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
